import React, { useEffect, useState } from 'react';
import { api } from '../lib/api.js';
import { useAuthStore } from '../stores/useAuthStore.js';
import { connectSocket, getSocket } from '../lib/socket.js';
import ReactMarkdown from 'react-markdown';

export default function Announcements() {
  const { user } = useAuthStore();
  const [announcements, setAnnouncements] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [content, setContent] = useState('');
  const [posting, setPosting] = useState(false);

  useEffect(() => {
    api.get<{ allowed: boolean }>('/api/admin/access')
      .then(({ data }) => setIsAdmin(data.allowed))
      .catch(() => setIsAdmin(false));

    api.get('/api/announcements')
      .then(({ data }) => setAnnouncements(data))
      .catch(console.error)
      .finally(() => setIsLoading(false));

    connectSocket();
    const socket = getSocket();
    socket.on('announcement:new', (announcement: any) => {
      setAnnouncements((prev) => [announcement, ...prev]);
    });
    return () => {
      socket.off('announcement:new');
    };
  }, []);

  const handlePost = async () => {
    if (!content.trim()) return;
    setPosting(true);
    try {
      await api.post('/api/announcements', { content });
      setContent('');
    } catch {
      alert('Failed to post announcement');
    } finally {
      setPosting(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this announcement?')) return;
    try {
      await api.delete(`/api/announcements/${id}`);
      setAnnouncements((prev) => prev.filter((a) => a.id !== id));
    } catch {
      alert('Failed to delete');
    }
  };

  const formatDate = (date: string) =>
    new Date(date).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });

  return (
    <div className="flex-1 min-h-screen bg-background text-on-surface">
      <div className="mb-8">
        <h1 className="font-headline-lg text-headline-lg text-on-surface mb-2">Announcements</h1>
        <p className="text-on-surface-variant text-body-sm">
          Official updates from the PVP Academy team.
        </p>
      </div>

      {isAdmin && (
        <div className="bg-[#141414] thin-border rounded-lg p-4 mb-8">
          <div className="text-xs uppercase tracking-widest text-error font-bold mb-3">
            Post Announcement
          </div>
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder={'Use markdown:\n## Big heading\n**bold text**\n- list item'}
            rows={5}
            className="w-full bg-[#0f1112] border border-outline-variant rounded px-3 py-2 text-on-surface text-sm resize-none outline-none focus:border-error font-mono"
          />
          <div className="flex justify-between items-center mt-3">
            <span className="text-xs text-on-surface-variant">
              Markdown supported — ## heading, **bold**, *italic*, - list
            </span>
            <button
              onClick={handlePost}
              disabled={posting || !content.trim()}
              className="px-4 py-2 bg-error text-white rounded text-sm font-bold disabled:opacity-50 active:scale-[0.97]"
            >
              {posting ? 'Posting…' : 'Post'}
            </button>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-32 bg-surface-variant rounded-lg animate-pulse" />
          ))}
        </div>
      ) : announcements.length === 0 ? (
        <div className="bg-[#141414] thin-border rounded-lg p-8 text-center text-on-surface-variant">
          No announcements yet.
        </div>
      ) : (
        <div className="space-y-4">
          {announcements.map((a) => (
            <div key={a.id} className="bg-[#141414] thin-border rounded-lg p-5">
              <div className="flex justify-between items-start mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-xs px-2 py-0.5 bg-[rgba(224,60,60,0.15)] text-error rounded font-bold uppercase tracking-wider">
                    {a.postedBy}
                  </span>
                  <span className="text-xs text-on-surface-variant">{formatDate(a.createdAt)}</span>
                </div>
                {isAdmin && (
                  <button
                    onClick={() => handleDelete(a.id)}
                    className="text-xs text-red-400 hover:text-red-300"
                  >
                    Delete
                  </button>
                )}
              </div>
              <div className="prose prose-invert prose-sm max-w-none
                prose-headings:text-on-surface prose-headings:font-bold
                prose-h1:text-2xl prose-h2:text-xl prose-h3:text-lg
                prose-strong:text-on-surface prose-em:text-on-surface-variant
                prose-li:text-on-surface-variant prose-p:text-on-surface-variant
                prose-a:text-error">
                <ReactMarkdown>{a.content}</ReactMarkdown>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}